import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Download, ExternalLink, Save, Clock, User } from 'lucide-react';

export default function DownloadCenter() {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    label: 'Download Center',
    description: ''
  });
  const [currentConfig, setCurrentConfig] = useState(null);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchDownloadCenter();
  }, []);

  const fetchDownloadCenter = async () => {
    try {
      const response = await axios.get(`${API_URL}/download-center`, {
        headers: getAuthHeaders()
      });
      setCurrentConfig(response.data);
      if (response.data) {
        setFormData({
          url: response.data.url || '',
          label: response.data.label || 'Download Center',
          description: response.data.description || ''
        });
      }
    } catch (error) {
      toast.error('Failed to fetch download center configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(formData.url);
    } catch {
      toast.error('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/download-center`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Download Center updated successfully');
      setCurrentConfig(response.data.value);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update download center');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';

  if (!isAdmin) {
    return (
      <Sidebar>
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-12">
            <p className="text-slate-500">You don't have permission to access this page.</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Download Center
          </h1>
          <p className="text-slate-600 mt-2">Manage the download center link for all users</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Current Configuration Card */}
            {currentConfig?.url && (
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Download className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Current Download Link</h2>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-purple-900">{currentConfig.label}</p>
                      <a 
                        href={currentConfig.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 truncate"
                      >
                        {currentConfig.url}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                      {currentConfig.description && (
                        <p className="text-xs text-purple-700 mt-1">{currentConfig.description}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(currentConfig.url, '_blank')}
                      className="ml-4 border-purple-300 text-purple-700 hover:bg-purple-100"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open
                    </Button>
                  </div>
                  {currentConfig.updated_at && (
                    <div className="mt-3 pt-3 border-t border-purple-200 flex items-center gap-4 text-xs text-purple-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Updated: {new Date(currentConfig.updated_at).toLocaleString()}
                      </span>
                      {currentConfig.updated_by_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          By: {currentConfig.updated_by_name}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Edit Form */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {currentConfig?.url ? 'Update Download Link' : 'Set Download Link'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="url">Download URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://drive.google.com/your-folder-link"
                    required
                    data-testid="download-url-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter the full URL (e.g., Google Drive, Dropbox, or any web link)
                  </p>
                </div>

                <div>
                  <Label htmlFor="label">Button Label</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="Download Center"
                    data-testid="download-label-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of what users can download"
                    rows={2}
                    data-testid="download-description-input"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="save-download-center"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : (currentConfig?.url ? 'Update Link' : 'Save Link')}
                  </Button>
                </div>
              </form>
            </div>

            {/* Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">How it works</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>The download link will appear in the sidebar for all users</li>
                <li>Agencies and Master Agencies can click to open the download link</li>
                <li>Only Admin and Sub Admin can modify this link</li>
                <li>Use Google Drive, Dropbox, or any publicly accessible URL</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
}
